import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Button, Typography, Card, CardContent, CardHeader, TextField, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TagsDropdown from '../TagsDropdown';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';

const SettingManualTag = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const [template, setTemplate] = useState('');
  const [outputTag, setOutputTag] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [outputTagAnchorEl, setOutputTagAnchorEl] = useState(null);
  const tagEditorRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'manualtag',
    onSaveStatusChange,
    customComparison: (initial) => {
      return template === (initial.template || '') &&
             outputTag === (initial.outputTag || '');
    },
    customSaveData: () => ({
      template: template,
      outputTag: outputTag,
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      if (data.customData?.template !== undefined) {
        setTemplate(data.customData.template);
      }
      if (data.customData?.outputTag !== undefined) {
        setOutputTag(data.customData.outputTag);
      }
    }
  });

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTagMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOutputTagMenuOpen = (event) => {
    setOutputTagAnchorEl(event.currentTarget);
  };

  const handleOutputTagMenuClose = () => {
    setOutputTagAnchorEl(null);
  };

  const handleOutputTagSelect = (value) => {
    setOutputTag(value);
    setOutputTagAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    if (tagEditorRef.current) {
        tagEditorRef.current.insertTag(value);
    }
    setAnchorEl(null);
  };

  return (
    <ActionSettingsLayout
      title="Manual Tag Generation"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      {/* Template Editor */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                <TextFieldsIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                TEMPLATE
              </Box>
              Tag Template
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: 2 }}>
          <Typography sx={{ mb: 1.5, fontSize: '0.9rem', color: '#666' }}>
            Define the content of the new tag. You can use existing tags as placeholders that will be replaced with their actual values.
          </Typography>
          
          <TagEditor
            ref={tagEditorRef}
            value={template}
            onChange={setTemplate}
            placeholder="e.g. The client {client_name} has requested {service_type}."
            className="manual-tag-editor"
            onTagClick={handleMenuOpen}
            tags={tags}
          />
        </CardContent>
      </Card>

      {/* Output Configuration */}
      <Card sx={{
        mb: 0,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                OUTPUT
              </Box>
              Result Tag
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ mb: 0 }}>
            <Typography sx={{
              fontWeight: 600,
              color: '#444',
              fontSize: '0.9rem',
              mb: 1,
            }}>
              Output Tag Name
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                value={outputTag}
                onChange={(e) => setOutputTag(e.target.value)}
                placeholder="e.g. summary_title, concatenated_info"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleOutputTagMenuOpen}
                startIcon={<AddIcon />}
                sx={{
                  color: '#5757b0',
                  borderColor: '#5757b0',
                  fontWeight: 600,
                  minWidth: 'auto',
                  padding: '6px 12px',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(87, 87, 176, 0.05)',
                    borderColor: '#5757b0',
                  },
                }}
              >
                Select Existing
              </Button>
            </Box>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#666',
              mt: 1,
              fontStyle: 'italic',
            }}>
              The generated text will be stored in this tag. If it doesn't exist, it will be created.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Tag dropdown for template editor */}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleTagMenuClose}
      />

      {/* Tag dropdown for output tag */}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleOutputTagSelect}
        anchorEl={outputTagAnchorEl}
        setAnchorEl={handleOutputTagMenuClose}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingManualTag);
